import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  input,
  output,
} from '@angular/core';
import { NgpButton } from 'ng-primitives/button';

/** Which role the button plays. Selects a palette, not a shape. */
export type ButtonSeverity =
  | 'primary'
  | 'secondary'
  | 'success'
  | 'info'
  | 'warn'
  | 'danger';

/** How much visual weight the button carries. Orthogonal to severity. */
export type ButtonVariant = 'filled' | 'outlined' | 'text';

export type ButtonSize = 'sm' | 'md' | 'lg';

/**
 * A button.
 *
 * Behaviour comes from ng-primitives' NgpButton, which handles the
 * disabled semantics and publishes interaction state as data-hover,
 * data-press, data-focus-visible and data-disabled on the host. Style
 * against those attributes rather than :hover/:active so the visuals
 * agree with what the primitive actually considers to be happening.
 *
 * severity × variant × size compose freely, and `iconOnly` is a fourth
 * axis on top of them rather than a value inside one. The source design
 * system ships a separate IconButton with its own solid/soft/ghost
 * variants, but those are just its Button's palettes applied to a
 * square — filled, outlined and text already say the same thing here.
 * What that component actually adds is shape, so that is all `iconOnly`
 * changes; folding it into `variant` would have meant giving up
 * outlined or text icon buttons to gain a square one.
 */
@Component({
  selector: 'hh-button',
  standalone: true,
  imports: [NgpButton],
  template: `
    <button
      ngpButton
      [type]="type()"
      [class]="classes()"
      [disabled]="isDisabled()"
      [attr.aria-busy]="loading() ? 'true' : null"
      [attr.aria-label]="ariaLabel() || null"
      [attr.title]="ariaLabel() || null"
      (click)="clicked.emit()"
    >
      @if (loading()) {
        <span class="hh-button__spinner" aria-hidden="true"></span>
      }
      @if (label()) {
        <span class="hh-button__label">{{ label() }}</span>
      }
      <ng-content />
    </button>
  `,
  styleUrl: './hh-button.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HhButtonComponent {
  readonly label = input('');
  readonly loading = input(false, { transform: booleanAttribute });
  readonly disabled = input(false, { transform: booleanAttribute });
  readonly type = input<'button' | 'submit' | 'reset'>('button');
  readonly severity = input<ButtonSeverity>('primary');
  readonly variant = input<ButtonVariant>('filled');
  readonly size = input<ButtonSize>('md');
  readonly fullWidth = input(false, { transform: booleanAttribute });

  /** Square, label-less: the content is a glyph. Needs `ariaLabel`. */
  readonly iconOnly = input(false, { transform: booleanAttribute });

  /**
   * Accessible name, and the hover tooltip.
   *
   * Only needed when nothing else names the button — an iconOnly one,
   * or a labelled one whose visible text is too terse to stand alone.
   */
  readonly ariaLabel = input('');

  readonly clicked = output<void>();

  /** A loading button is not a button you should be able to press. */
  protected readonly isDisabled = computed(() => this.disabled() || this.loading());

  protected readonly classes = computed(() =>
    [
      'hh-button',
      `hh-button--${this.severity()}`,
      `hh-button--${this.variant()}`,
      `hh-button--${this.size()}`,
      this.iconOnly() ? 'hh-button--icon' : '',
      this.fullWidth() ? 'hh-button--full' : '',
    ]
      .filter(Boolean)
      .join(' '),
  );

  constructor() {
    // An icon-only button with no ariaLabel and no label reaches screen
    // readers as an unnamed button — the glyph inside is decorative
    // markup, not text. Cheap to get wrong and invisible on screen, so
    // it is worth saying out loud.
    effect(() => {
      if (this.iconOnly() && !this.ariaLabel() && !this.label()) {
        console.warn('hh-button: iconOnly needs an ariaLabel — this button has no accessible name.');
      }
    });
  }
}
